import type { ErrorEvent } from "@sentry/react";

/**
 * Strips personally identifiable information (PII) from a Sentry event before
 * it leaves the browser.
 *
 * We run with `sendDefaultPii: false`, which already prevents Sentry from
 * automatically attaching things like IP addresses and cookies. This function
 * is a defense-in-depth pass that scrubs any PII that instrumentation,
 * integrations, or breadcrumbs may still have collected so that regulated-user
 * data does not egress to our error-tracking vendor.
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  // User context: keep only a stable id for correlation, drop direct
  // identifiers (IP, email, username, geo, etc.).
  if (event.user) {
    if (event.user.id) {
      event.user = { id: event.user.id };
    } else {
      delete event.user;
    }
  }

  // Request context: cookies, headers, and query strings frequently carry
  // auth tokens and form input, and the URL query can carry PII/UTMs.
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.query_string;
    delete event.request.data;
  }

  // Server name can leak host/user details in some environments.
  delete event.server_name;

  return event;
}
